import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, CheckCircle, XCircle, ExternalLink, Upload, ShoppingCart, Trash2, Package, ChevronRight } from 'lucide-react';
import axios from 'axios';
import PurchaseProofViewer from './PurchaseProofViewer';
import RequestPhotos from './RequestPhotos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${BACKEND_URL}${url}`;

const STATUS_BADGE = {
  pending:   'bg-yellow-100 text-yellow-700',
  approved:  'bg-blue-100 text-blue-700',
  rejected:  'bg-red-100 text-red-700',
  purchased: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL = { pending: 'Menunggu', approved: 'Disetujui', rejected: 'Ditolak', purchased: 'Sudah Dibeli', cancelled: 'Dibatalkan' };

const CATEGORY_BADGE = {
  konsumsi:     'bg-orange-100 text-orange-700',
  perlengkapan: 'bg-blue-100 text-blue-700',
  peralatan:    'bg-purple-100 text-purple-700',
  utilitas:     'bg-teal-100 text-teal-700',
};
const CATEGORY_LABEL = { konsumsi: 'Konsumsi', perlengkapan: 'Perlengkapan', peralatan: 'Peralatan', utilitas: 'Utilitas' };

const fmt = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const emptyItem = () => ({ item_name: '', purchase_link: '', qty: 1, price: '' });

export default function PurchaseRequests({ userRole, adminName, adminEmail, userPropertyName }) {
  const fixedProperty = String(adminEmail || '').toLowerCase() === 'penjagakmy@iskhouse.id' ? 'Gedung Kemayoran' : (userRole === 'housekeeping' ? userPropertyName : null);
  const isApprover = userRole === 'superadmin';
  const canCreate = ['admin', 'superadmin', 'technician', 'housekeeping'].includes(userRole);
  const canBatch = ['admin', 'superadmin'].includes(userRole);

  const [requests, setRequests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');
  const [showForm, setShowForm] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [batchForm, setBatchForm] = useState({ selected: {}, finalPrices: {}, shippingCost: '', notes: '', proof: null });
  const [lightboxImg, setLightboxImg] = useState(null);
  const [purchaseProof, setPurchaseProof] = useState(null);

  const initForm = () => ({
    property_name: fixedProperty || '', location_type: 'umum', room_number: '', location_detail: '',
    transfer_account: '', transfer_name: '',
    category: 'perlengkapan', items: [emptyItem()], notes: '', photos: [],
  });
  const [form, setForm] = useState(initForm());
  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = async () => {
    try {
      const calls = [
        axios.get(`${BACKEND_URL}/api/purchase-requests`, config()),
        axios.get(`${BACKEND_URL}/api/staff/properties`, config()),
      ];
      if (canBatch) calls.push(axios.get(`${BACKEND_URL}/api/purchase-batches`, config()));
      const [reqRes, propRes, batchRes] = await Promise.all(calls);
      setRequests(reqRes.data);
      setProperties(propRes.data);
      if (batchRes) setBatches(batchRes.data);
    } catch (e) {
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (fixedProperty) setForm(f => ({ ...f, property_name: fixedProperty }));
  }, [fixedProperty]);

  const updateItem = (idx, field, val) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [field]: val } : it) }));
  };
  const grandTotal = form.items.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.price || 0)), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('property_name', fixedProperty || form.property_name);
      fd.append('location_type', form.location_type);
      fd.append('room_number', form.room_number || '');
      fd.append('location_detail', form.location_detail || '');
      fd.append('transfer_account', form.transfer_account || '');
      fd.append('transfer_name', form.transfer_name || '');
      fd.append('category', form.category);
      fd.append('notes', form.notes || '');
      fd.append('items', JSON.stringify(form.items));
      form.photos.forEach(f => fd.append('photos', f));
      await axios.post(`${BACKEND_URL}/api/purchase-requests`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Pengajuan berhasil dibuat!');
      setShowForm(false); setForm(initForm()); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal membuat pengajuan'); }
    finally { setSubmitting(false); }
  };

  const handleCancelApproval = async (requestId) => {
    if (!window.confirm('Batalkan persetujuan pengajuan ini? Status akan kembali ke Menunggu.')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${requestId}/cancel-approval`, {}, config());
      setSuccess('Persetujuan berhasil dibatalkan');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal membatalkan'); }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Hapus pengajuan ini secara permanen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-requests/${requestId}`, config());
      setSuccess('Pengajuan berhasil dihapus');
      setSelectedRequest(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menghapus'); }
  };

  const handleCancelOwn = async (requestId) => {
    if (!window.confirm('Batalkan pengajuan ini? Tindakan ini tidak bisa dibatalkan.')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${requestId}/cancel`, {}, config());
      setSuccess('Pengajuan berhasil dibatalkan');
      setSelectedRequest(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal membatalkan pengajuan'); }
  };

  const handleApprove = async () => {
    setError(''); setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${showApproveModal.request_id}/approve`, {}, config());
      setSuccess('Pengajuan disetujui!');
      setShowApproveModal(null); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyetujui'); }
    finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    setError(''); setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${showRejectModal}/reject`, { rejection_reason: rejectionReason }, config());
      setSuccess('Pengajuan ditolak.');
      setShowRejectModal(null); setRejectionReason(''); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menolak'); }
    finally { setSubmitting(false); }
  };

  const approvedUnbatched = requests.filter(r => r.status === 'approved' && !r.batch_id);

  const toggleBatchSelect = (rid, totalPrice) => {
    setBatchForm(f => {
      const next = { ...f.selected };
      if (next[rid]) { delete next[rid]; const np = { ...f.finalPrices }; delete np[rid]; return { ...f, selected: next, finalPrices: np }; }
      else { next[rid] = true; return { ...f, selected: next, finalPrices: { ...f.finalPrices, [rid]: String(totalPrice) } }; }
    });
  };

  const batchSelectedIds = Object.keys(batchForm.selected);
  const batchItemsTotal = batchSelectedIds.reduce((s, rid) => s + parseFloat(batchForm.finalPrices[rid] || 0), 0);
  const batchGrandTotal = batchItemsTotal + parseFloat(batchForm.shippingCost || 0);

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    if (!batchSelectedIds.length) { setError('Pilih minimal 1 pengajuan'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('request_ids', JSON.stringify(batchSelectedIds));
      fd.append('final_prices', JSON.stringify(batchForm.finalPrices));
      fd.append('shipping_cost', batchForm.shippingCost || '0');
      fd.append('notes', batchForm.notes || '');
      if (batchForm.proof) fd.append('proof', batchForm.proof);
      await axios.post(`${BACKEND_URL}/api/purchase-batches`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Batch pembelian berhasil dibuat!');
      setShowBatchModal(false);
      setBatchForm({ selected: {}, finalPrices: {}, shippingCost: '', notes: '', proof: null });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal membuat batch'); }
    finally { setSubmitting(false); }
  };

  const filtered = filterStatus === 'all' ? requests : requests.filter(r => r.status === filterStatus);

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-bold text-isk-text flex items-center gap-2 shrink-0">
          <ShoppingCart className="w-5 h-5 text-isk-teal" /> Pengajuan Pembelian
        </h2>
        <div className="flex gap-2 items-center shrink-0">
          {canBatch && approvedUnbatched.length > 0 && (
            <Button onClick={() => { setError(''); setShowBatchModal(true); }} size="sm" className="bg-isk-gold hover:bg-isk-gold/80 text-white rounded-full">
              <Package className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Buat Pembelian</span>
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setShowForm(true)} size="sm" className="bg-isk-teal text-white rounded-full">
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Buat Pengajuan</span>
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {canBatch && (
        <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit">
          <button onClick={() => setActiveTab('requests')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'requests' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
            Pengajuan
          </button>
          <button onClick={() => setActiveTab('batches')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'batches' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
            Pembelian ({batches.length})
          </button>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <>
          <div className="flex gap-2 items-center">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 rounded-full text-xs h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="pending">Menunggu</SelectItem>
                <SelectItem value="approved">Disetujui</SelectItem>
                <SelectItem value="purchased">Sudah Dibeli</SelectItem>
                <SelectItem value="rejected">Ditolak</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-isk-muted">{filtered.length} pengajuan</p>
          </div>
          <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
            <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
              <span>Nama Barang</span><span>Kategori</span><span>Properti</span><span>Total</span><span>Status</span><span></span>
            </div>
            {filtered.length === 0 && <div className="text-center py-12 text-isk-muted">Belum ada pengajuan</div>}
            <div className="divide-y divide-white/20">
            {filtered.map((r) => {
              const itemList = r.items || (r.item_name ? [{ item_name: r.item_name, purchase_link: r.purchase_link, qty: r.qty, price: r.price }] : []);
              const isSelected = selectedRequest?.request_id === r.request_id;
              return (
                <div key={r.request_id} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center hover:bg-white/30 transition-colors">
                  <div>
                    <p className="font-semibold text-isk-text text-sm truncate">{r.item_name}</p>
                    <p className="text-xs text-isk-muted">{r.created_by_name || r.created_by} · {fmtDate(r.created_at)}</p>
                  </div>
                  <div className="hidden sm:block"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_BADGE[r.category] || 'bg-gray-100 text-gray-700'}`}>{CATEGORY_LABEL[r.category] || r.category || '-'}</span></div>
                  <p className="hidden sm:block text-xs text-isk-muted truncate">{r.property_name}</p>
                  <p className="hidden sm:block text-sm font-semibold text-isk-teal">{fmt(r.final_price || r.total_price)}</p>
                  <div className="hidden sm:block"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span></div>
                  <Button size="sm" onClick={() => setSelectedRequest(r)}
                    className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                    {r.request_photos ? 'Detail & Foto' : 'Detail'} <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                  </Button>

                  {r.status === 'approved' && (
                    <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-xs text-blue-700">Disetujui oleh {r.approved_by} · {fmtDate(r.approved_at)}</p>
                      {isApprover && (
                        <button onClick={() => handleCancelApproval(r.request_id)}
                          className="shrink-0 text-xs text-orange-600 hover:text-orange-800 font-medium underline">
                          Batalkan
                        </button>
                      )}
                    </div>
                  )}
                  {r.status === 'purchased' && r.batch_id && (
                    <div className="p-2 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">
                      Dibeli dalam batch <span className="font-mono font-bold">{r.batch_id}</span>
                      {r.final_price && r.final_price !== r.total_price && <span> · Harga final: {fmt(r.final_price)}</span>}
                    </div>
                  )}
                  {r.status === 'rejected' && (
                    <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                      Ditolak oleh {r.approved_by} · {fmtDate(r.approved_at)}
                      {r.rejection_reason && <span> · Alasan: {r.rejection_reason}</span>}
                    </div>
                  )}

                </div>
              );
            })}
            </div>
          </div>

          {/* Detail Popup */}
          {selectedRequest && (() => {
            const r = selectedRequest;
            const itemList = r.items || (r.item_name ? [{ item_name: r.item_name, purchase_link: r.purchase_link, qty: r.qty, price: r.price }] : []);
            return (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
                  <div className="flex justify-between items-start p-5 pb-4 border-b border-gray-100">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-isk-text">{r.item_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                        {r.category && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_BADGE[r.category] || 'bg-gray-100 text-gray-700'}`}>{CATEGORY_LABEL[r.category] || r.category}</span>}
                      </div>
                      <p className="text-xs text-isk-muted mt-0.5">{r.request_id} · {fmtDate(r.created_at)} · {r.created_by_name || r.created_by}</p>
                    </div>
                    <button onClick={() => setSelectedRequest(null)}><X className="w-5 h-5" /></button>
                  </div>
                  <div className="overflow-y-auto flex-1 p-5 space-y-3">
                    {/* Info */}
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-gray-50"><p className="text-isk-muted">Properti</p><p className="font-medium">{r.property_name}</p></div>
                      <div className="p-2 rounded-lg bg-gray-50"><p className="text-isk-muted">Lokasi</p><p className="font-medium">{r.location_type === 'kamar' ? `Kamar ${r.room_number}` : 'Umum'}{r.location_detail && ` · ${r.location_detail}`}</p></div>
                      <div className="p-2 rounded-lg bg-gray-50 text-right"><p className="text-isk-muted">Total</p><p className="font-bold text-isk-teal">{fmt(r.final_price || r.total_price)}</p>{r.final_price && r.final_price !== r.total_price && <p className="text-xs line-through text-isk-muted">{fmt(r.total_price)}</p>}</div>
                    </div>
                    {/* Items */}
                    {itemList.length > 0 && (
                      <div className="space-y-1">
                        {itemList.map((it, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs p-2 rounded-lg bg-blue-50 border border-blue-100">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-isk-text">{it.item_name}</span>
                              {it.purchase_link && <a href={it.purchase_link} target="_blank" rel="noreferrer" className="ml-2 text-isk-teal inline-flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> Link</a>}
                            </div>
                            <span className="text-isk-muted ml-2 shrink-0">{it.qty} × {fmt(it.price)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.transfer_account && <div className="p-2 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">Rekening: <span className="font-medium">{r.transfer_account}</span>{r.transfer_name && ` a.n. ${r.transfer_name}`}</div>}
                    {r.notes && <p className="text-xs text-isk-muted italic">Catatan: {r.notes}</p>}
                    {r.status === 'approved' && <div className="p-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 flex items-center justify-between"><span>Disetujui oleh {r.approved_by} · {fmtDate(r.approved_at)}</span>{isApprover && <button onClick={() => { handleCancelApproval(r.request_id); setSelectedRequest(null); }} className="text-orange-500 hover:text-orange-700 underline ml-2 shrink-0">Batalkan</button>}</div>}
                    {r.status === 'purchased' && r.batch_id && <div className="p-2 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">Dibeli dalam batch <span className="font-mono font-bold">{r.batch_id}</span></div>}
                    {r.status === 'rejected' && <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">Ditolak oleh {r.approved_by}{r.rejection_reason && ` · ${r.rejection_reason}`}</div>}
                    <RequestPhotos value={r.request_photos} onOpen={setLightboxImg} />
                  </div>
                  {isApprover && r.status === 'pending' && (
                    <div className="p-5 pt-4 border-t border-gray-100 flex gap-2">
                      <Button size="sm" onClick={() => { setSelectedRequest(null); setShowApproveModal(r); }} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Setujui
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setSelectedRequest(null); setShowRejectModal(r.request_id); }} className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Tolak
                      </Button>
                    </div>
                  )}
                  {!isApprover && r.status === 'pending' && r.created_by === adminEmail && (
                    <div className="p-5 pt-4 border-t border-gray-100">
                      <Button size="sm" variant="outline" onClick={() => handleCancelOwn(r.request_id)}
                        className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Batalkan Pengajuan
                      </Button>
                    </div>
                  )}
                  {((isApprover && (r.status === 'rejected' || r.status === 'cancelled')) ||
                    (!isApprover && r.status === 'cancelled' && r.created_by === adminEmail)) && (
                    <div className="p-5 pt-4 border-t border-gray-100">
                      <Button size="sm" variant="outline" onClick={() => handleDelete(r.request_id)}
                        className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Pengajuan
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && canBatch && (
        <div className="space-y-3">
          {batches.length === 0 && <div className="text-center py-12 text-isk-muted">Belum ada pembelian</div>}
          {batches.map((b) => {
            const batchReqs = requests.filter(r => r.batch_id === b.batch_id);
            return (
              <div key={b.batch_id} className="glass-card rounded-2xl p-4 space-y-3 border border-white/40">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono font-bold text-isk-teal">{b.batch_id}</p>
                    <p className="text-xs text-isk-muted mt-0.5">{fmtDate(b.created_at)} · oleh {b.created_by_name || b.created_by}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-isk-text">{fmt(b.total_amount)}</p>
                    {b.shipping_cost > 0 && <p className="text-xs text-isk-muted">Ongkir: {fmt(b.shipping_cost)}</p>}
                  </div>
                </div>

                <div className="space-y-1">
                  {batchReqs.map(r => (
                    <div key={r.request_id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-white/50">
                      <div>
                        <span className="font-medium text-isk-text">{r.item_name}</span>
                        <span className="text-isk-muted ml-2">{r.property_name}</span>
                      </div>
                      <span className="font-medium text-green-600">{fmt(r.final_price || r.total_price)}</span>
                    </div>
                  ))}
                </div>

                {b.notes && <p className="text-xs text-isk-muted italic">Catatan: {b.notes}</p>}

                {b.proof_url && (
                  <button type="button" onClick={() => setPurchaseProof(resolveImg(b.proof_url))} className="inline-flex items-center gap-1 text-xs text-isk-teal hover:underline">
                    <ExternalLink className="w-3 h-3" /> Lihat Bukti Pembelian
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Request Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">Buat Pengajuan Pembelian</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3" noValidate>
              <div>
                <Label>Kost / Properti *</Label>
                {fixedProperty ? <Input aria-label="Kost / Properti" value={fixedProperty} readOnly className="mt-1 rounded-xl bg-gray-100" /> : <Select value={form.property_name} onValueChange={(v) => setForm({ ...form, property_name: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih properti" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                </Select>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Lokasi *</Label>
                  <Select value={form.location_type} onValueChange={(v) => setForm({ ...form, location_type: v, room_number: '' })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="umum">Area Umum</SelectItem>
                      <SelectItem value="kamar">Kamar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kategori *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="konsumsi">Konsumsi</SelectItem>
                      <SelectItem value="perlengkapan">Perlengkapan</SelectItem>
                      <SelectItem value="peralatan">Peralatan</SelectItem>
                      <SelectItem value="utilitas">Utilitas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.location_type === 'kamar' ? (
                <div><Label>Nomor Kamar *</Label><Input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} placeholder="Contoh: 101" className="mt-1 rounded-xl" /></div>
              ) : (
                <div><Label>Lokasi Spesifik</Label><Input value={form.location_detail} onChange={(e) => setForm({ ...form, location_detail: e.target.value })} placeholder="Lobby, Tangga Lt.2, Dapur Bersama" className="mt-1 rounded-xl" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>No. Rekening Tujuan</Label><Input value={form.transfer_account} onChange={(e) => setForm({ ...form, transfer_account: e.target.value })} placeholder="Opsional" className="mt-1 rounded-xl" /></div>
                <div><Label>Nama Penerima</Label><Input value={form.transfer_name} onChange={(e) => setForm({ ...form, transfer_name: e.target.value })} placeholder="Opsional" className="mt-1 rounded-xl" /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Daftar Barang *</Label>
                  <button type="button" onClick={() => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))} className="flex items-center gap-1 text-xs text-isk-teal hover:underline font-medium">
                    <Plus className="w-3.5 h-3.5" /> Tambah Barang
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="p-3 rounded-xl border border-gray-200 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-isk-muted">Barang {idx + 1}</span>
                        {form.items.length > 1 && <button type="button" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                      <Input value={it.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} placeholder="Nama barang *" className="rounded-lg text-sm" />
                      <Input value={it.purchase_link} onChange={(e) => updateItem(idx, 'purchase_link', e.target.value)} placeholder="Link pembelian (opsional)" className="rounded-lg text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" min="1" value={it.qty} onChange={(e) => updateItem(idx, 'qty', e.target.value)} placeholder="Qty *" className="rounded-lg text-sm" />
                        <Input type="number" min="0" value={it.price} onChange={(e) => updateItem(idx, 'price', e.target.value)} placeholder="Est. harga satuan *" className="rounded-lg text-sm" />
                      </div>
                      {it.qty && it.price && !isNaN(parseFloat(it.price)) && <p className="text-xs text-isk-teal font-medium">Subtotal: {fmt(parseFloat(it.qty) * parseFloat(it.price))}</p>}
                    </div>
                  ))}
                </div>
              </div>
              {grandTotal > 0 && (
                <div className="p-3 rounded-xl bg-isk-teal/10 text-isk-teal font-semibold text-sm flex justify-between">
                  <span>Total Estimasi</span><span>{fmt(grandTotal)}</span>
                </div>
              )}
              <div><Label>Catatan</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opsional" className="mt-1 rounded-xl" /></div>
              <div>
                <Label>Foto Referensi (maks. 5 foto)</Label>
                <label className="mt-1 flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-isk-teal/30 rounded-xl cursor-pointer hover:bg-isk-teal/5">
                  <Upload className="w-5 h-5 text-isk-muted mb-1" />
                  <span className="text-xs text-isk-muted">{form.photos.length > 0 ? `${form.photos.length} foto dipilih` : 'Klik untuk upload foto (opsional)'}</span>
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      const files = Array.from(e.target.files || []).slice(0, 5);
                      setForm(f => ({ ...f, photos: files }));
                    }} />
                </label>
                {form.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {form.photos.map((file, i) => (
                      <div key={i} className="relative">
                        <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                        <button type="button" onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-isk-teal text-white rounded-full">{submitting ? 'Mengirim...' : 'Kirim Pengajuan'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Purchase Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">Buat Pembelian</h3>
              <button onClick={() => setShowBatchModal(false)}><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs">{error}</div>}
            <form onSubmit={handleBatchSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Pilih Pengajuan yang Disetujui</Label>
                <p className="text-xs text-isk-muted mb-2">{approvedUnbatched.length} pengajuan tersedia</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {approvedUnbatched.map(r => (
                    <div key={r.request_id} className={`p-3 rounded-xl border-2 cursor-pointer transition-colors ${batchForm.selected[r.request_id] ? 'border-isk-teal bg-isk-teal/5' : 'border-gray-200 bg-gray-50'}`}
                      onClick={() => toggleBatchSelect(r.request_id, r.total_price)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={!!batchForm.selected[r.request_id]} readOnly className="accent-isk-teal" />
                          <div>
                            <p className="text-sm font-medium text-isk-text">{r.item_name}</p>
                            <p className="text-xs text-isk-muted">{r.property_name} · {r.request_id}</p>
                          </div>
                        </div>
                        <span className="text-xs text-isk-muted font-medium">{fmt(r.total_price)}</span>
                      </div>
                      {batchForm.selected[r.request_id] && (
                        <div className="mt-2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Label className="text-xs shrink-0">Harga Final:</Label>
                          <Input
                            type="number" min="0"
                            value={batchForm.finalPrices[r.request_id] || ''}
                            onChange={e => setBatchForm(f => ({ ...f, finalPrices: { ...f.finalPrices, [r.request_id]: e.target.value } }))}
                            placeholder={String(r.total_price)}
                            className="h-7 text-xs rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {batchSelectedIds.length > 0 && (
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-isk-muted">Total Item</span><span className="font-medium">{fmt(batchItemsTotal)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-isk-muted">Ongkos Kirim</span>
                    <Input type="number" min="0" value={batchForm.shippingCost} onChange={e => setBatchForm(f => ({ ...f, shippingCost: e.target.value }))} placeholder="0" className="h-7 w-32 text-xs rounded-lg" />
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold text-isk-text">
                    <span>Total Pembelian</span><span>{fmt(batchGrandTotal)}</span>
                  </div>
                </div>
              )}

              <div>
                <Label>Bukti Pemesanan (foto/PDF)</Label>
                <label className="mt-1 flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-isk-teal/30 rounded-xl cursor-pointer hover:bg-isk-teal/5">
                  <Upload className="w-5 h-5 text-isk-muted mb-1" />
                  <span className="text-xs text-isk-muted">{batchForm.proof ? batchForm.proof.name : 'Klik untuk upload bukti (opsional)'}</span>
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setBatchForm(f => ({ ...f, proof: e.target.files?.[0] || null }))} />
                </label>
              </div>

              <div>
                <Label>Catatan</Label>
                <Input value={batchForm.notes} onChange={e => setBatchForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opsional" className="mt-1 rounded-xl" />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowBatchModal(false)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={submitting || !batchSelectedIds.length} className="flex-1 bg-isk-teal text-white rounded-full">
                  {submitting ? 'Menyimpan...' : `Buat Pembelian (${batchSelectedIds.length} item)`}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (() => {
        const r = showApproveModal;
        const itemList = r.items || (r.item_name ? [{ item_name: r.item_name, purchase_link: r.purchase_link, qty: r.qty, price: r.price }] : []);
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[85vh]">
              {/* Header — fixed */}
              <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-100">
                <h3 className="font-bold text-isk-text">Setujui Pengajuan</h3>
                <button onClick={() => setShowApproveModal(null)}><X className="w-5 h-5" /></button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1 p-6 space-y-3">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-isk-text">{r.item_name}</p>
                      <p className="text-xs text-isk-muted">{r.request_id} · {r.property_name}</p>
                    </div>
                    <p className="font-bold text-isk-teal text-sm">{fmt(r.total_price)}</p>
                  </div>
                  {r.notes && <p className="text-xs text-isk-muted italic">"{r.notes}"</p>}
                </div>

                {itemList.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-isk-muted">Detail Barang & Link Pembelian</p>
                    {itemList.map((it, idx) => (
                      <div key={idx} className="p-2.5 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-isk-text">{it.item_name}</p>
                          <p className="text-xs text-isk-muted">{it.qty} pcs · Est. {fmt(it.price)}/pcs</p>
                        </div>
                        {it.purchase_link ? (
                          <a href={it.purchase_link} target="_blank" rel="noreferrer"
                            className="shrink-0 flex items-center gap-1 text-xs text-white bg-isk-teal hover:bg-isk-dark px-2.5 py-1.5 rounded-lg font-medium">
                            <ExternalLink className="w-3.5 h-3.5" /> Cek Link
                          </a>
                        ) : (
                          <span className="text-xs text-isk-muted italic shrink-0">Tanpa link</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <RequestPhotos value={r.request_photos} onOpen={setLightboxImg} />
              </div>

              {/* Footer — fixed */}
              <div className="p-6 pt-4 border-t border-gray-100">
                {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowApproveModal(null)} className="flex-1 rounded-full">Batal</Button>
                  <Button onClick={handleApprove} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full">
                    {submitting ? 'Menyimpan...' : 'Setujui'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {purchaseProof && <PurchaseProofViewer url={purchaseProof} onClose={() => setPurchaseProof(null)} />}

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <button onClick={() => setLightboxImg(null)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60">
            <X className="w-5 h-5" />
          </button>
          <img src={lightboxImg} alt="Foto referensi" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">Tolak Pengajuan</h3>
              <button onClick={() => { setShowRejectModal(null); setRejectionReason(''); }}><X className="w-5 h-5" /></button>
            </div>
            <Label>Alasan Penolakan (opsional)</Label>
            <Input value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Misal: Harga terlalu mahal" className="mt-1 rounded-xl mb-4" />
            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowRejectModal(null); setRejectionReason(''); }} className="flex-1 rounded-full">Batal</Button>
              <Button onClick={handleReject} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full">
                {submitting ? 'Menyimpan...' : 'Tolak'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
